import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { EnableClassTelemetry } from 'app/services/otel.service';
import { SystemService } from 'app/services/system.service';
import { QueryParams } from '../api.service';
import {
  RealtimeConnection,
  RealtimeEventHandler,
  RealtimeMessage,
  RealtimeService,
} from '../realtime.service';
import { StorageAPIService } from '../storage-api.service';

export enum TaskType {
  EXPORT_DOCUMENTS = 'EXPORT_DOCUMENTS',
  DOCUMENT_DETAILS_REPORT = 'DOCUMENT_DETAILS_REPORT',
  QUERY_DETAILS_REPORT = 'QUERY_DETAILS_REPORT',
  CONCEPT_DETAILS_REPORT = 'CONCEPT_DETAILS_REPORT',
  THEME_DETAILS_REPORT = 'THEME_DETAILS_REPORT',
  ENTITY_DETAILS_REPORT = 'ENTITY_DETAILS_REPORT',
  ENTITY_THEME_DETAILS_REPORT = 'ENTITY_THEME_DETAILS_REPORT',
  TAXONOMY_DETAILS_REPORT = 'TAXONOMY_DETAILS_REPORT',
  AUDIT_REPORT = 'AUDIT_REPORT',
  DRIVER_ANALYSIS = 'DRIVER_ANALYSIS',
  REPORT_EXTRACTOR_REPORT = 'REPORT_EXTRACTOR_REPORT',
  SMART_SUMMARY_DOCUMENT = 'SMART_SUMMARY_DOCUMENT',
  COPY_COLLECTION = 'COPY_COLLECTION',
  DELETE_PROJECT = 'DELETE_PROJECT',
  DELETE_COLLECTION = 'DELETE_COLLECTION',
  DO_NOTHING = 'DO_NOTHING',
  GENERATE_EMBEDDINGS = 'GENERATE_EMBEDDINGS',
  USAGE_REPORT = 'USAGE_REPORT',
  INTERACTIVE_SMART_SUMMARY = 'INTERACTIVE_SMART_SUMMARY',
  AI_INSIGHT_PREP_TASK = 'AI_INSIGHT_PREP_TASK',
  AI_INSIGHT_COMPLETION_TASK = 'AI_INSIGHT_COMPLETION_TASK',
  CONFIGURATION_TEST_EVALUATION_TASK = 'CONFIGURATION_TEST_EVALUATION_TASK',
}

export interface TaskPayload {
  _type: TaskType;
  groupId?: string;
  metadata?: { [key: string]: string };
}

export interface Task<P extends TaskPayload, R> {
  id: string;
  groupId?: string;
  userId: string;
  type: TaskType;
  status: string;
  createdAt: Date;
  startedAt?: Date;
  finishedAt?: Date;
  taskPayload: P;
  taskResult?: R;
  progress: number;
  version: number;
}

export interface DownloadTaskPayload extends TaskPayload {
  projectId: string;
  collectionId?: string;
  analysisId?: string;
  analysisGroupId?: string;
  source: string;
  filename: string;
  format: string;
  params?: string;
  query?: string;
  filter?: string;
  size?: number;
  sortField: string;
  sortOrder: string;
}

export interface DownloadTaskResult {
  exportId: string;
  fileSize: number;
  docCount: number;
  message?: string;
}

export type DownloadTask = Task<DownloadTaskPayload, DownloadTaskResult>;

export interface DownloadTaskList {
  items: DownloadTask[];
  count: number;
  storageBytesUsed: number;
  storageBytesRemaining: number;
  aggregations: any;
}

@EnableClassTelemetry()
@Injectable()
export class DownloadTasksService extends StorageAPIService {
  constructor(
    public http: HttpClient,
    public systemService: SystemService,
    private realtimeService: RealtimeService
  ) {
    super(http, systemService);
  }

  public on<P extends TaskPayload, R>(
    namespace: string,
    fn: RealtimeEventHandler<Task<P, R>>
  ): RealtimeConnection {
    return this.realtimeService.on(
      namespace,
      {
        task: (data: RealtimeMessage<Task<P, R>>) => {
          fn(data);
        },
      },
      'downloadService'
    );
  }

  public async getDownloadTasks(params?: QueryParams): Promise<DownloadTaskList> {
    return this.get<DownloadTaskList>('/tasks', params);
  }

  public createTask<P extends TaskPayload, R>(payload: P): Promise<Task<P, R>> {
    return this.post<Task<P, R>>('/tasks', payload);
  }

  public async deleteDownloadTask(downloadTaskId: string): Promise<void> {
    return this.delete<void>(`/tasks/${downloadTaskId}`);
  }

  public downloadDocument(exportId: string): Promise<Blob> {
    return this.getBinary(`/exports/${exportId}`);
  }
}
